"use client";

import React from "react";
import pickBy from "lodash/pickBy";
import identity from "lodash/identity";
import { useAction } from "next-safe-action/hooks";
import { useParams, useRouter } from "next/navigation";
import { useFormContext } from "react-hook-form";
import { useAtom, useAtomValue, useSetAtom } from "jotai";

import { Button } from "@/components/ui/button";
import { Paragraph, Text } from "@/components/ui/typography";
import { Skeleton } from "@/components/ui/skeleton";

import { isAuthenticatedAtom, showLoginAtom } from "@/store/auth";
import { emiRequestModalAtom, userCourseDetailAtom } from "@/store/course";

import useMoengageEventTracker from "@/hooks/use-moengage";
import EVENT_NAME from "@/hooks/use-moengage/event-name";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute";

import useSourceScreen from "@/hooks/use-source-screen";

import { getUserCourseDetail } from "@/fetchers/course";

import { getCourseType, languageMapping } from "@/lib/utils";

function CourseActionButton({ course }) {
  // eslint-disable-next-line react-hooks/purity
  const courseTimeStamp = React.useRef(Date.now());

  const params = useParams();
  const router = useRouter();
  const form = useFormContext();
  const trackMoengageEvent = useMoengageEventTracker();
  const sourceScreen = useSourceScreen();

  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const setShowLogin = useSetAtom(showLoginAtom);
  const setIsOpen = useSetAtom(emiRequestModalAtom);
  const [userCourseDetail, setUserCourseDetail] = useAtom(userCourseDetailAtom);

  const offlineCentreId = form.watch("offlineCentreId");

  const {
    result: userCourseDetailResult,
    isIdle: isIdleUserCourseDetail,
    isExecuting: isFetchingUserCourseDetail,
    executeAsync: fetchUserCourseDetail,
  } = useAction(getUserCourseDetail);

  const {
    isExpired,
    isExpiring,
    isPurchased,
    paymentType,
    isCourseFree,
    validityLeftDays,
  } = React.useMemo(
    () => ({
      isExpired: userCourseDetail?.validityLeftDays < 0,
      isExpiring:
        userCourseDetail?.validityLeftDays >= 0 &&
        userCourseDetail?.validityLeftDays <= 15,
      isPurchased: userCourseDetail?.purchased,
      paymentType: userCourseDetail?.paymentType,
      isCourseFree: userCourseDetail?.isCourseFree,
      validityLeftDays: userCourseDetail?.validityLeftDays,
    }),
    [userCourseDetail]
  );

  React.useEffect(() => {
    if (userCourseDetailResult?.data) {
      setUserCourseDetail(userCourseDetailResult.data);
    }
  }, [userCourseDetailResult, setUserCourseDetail]);

  React.useEffect(() => {
    if (isAuthenticated) {
      fetchUserCourseDetail({
        courseSlug: params.slug,
        offlineCentreId,
      });
    }
  }, [isAuthenticated, offlineCentreId, fetchUserCourseDetail, params.slug]);

  const handleBuyNowClick =
    (action = 1) =>
    (event) => {
      if (!isAuthenticated) {
        event.preventDefault();
        setShowLogin(true);
        return;
      }

      const {
        mode,
        packageId,
        languageId,
        courseTypeId,
        offlineCentreId,
        couponCode,
      } = form.getValues();

      const queryParams = new URLSearchParams(
        pickBy(
          {
            mode,
            packageId,
            languageId,
            courseTypeId,
            couponCode,
            action,
          },
          identity
        )
      );

      if (mode === "offline") {
        queryParams.append("location", offlineCentreId);
      }

      try {
        trackMoengageEvent(EVENT_NAME.BUY_NOW_CLICKED, {
          [EVENT_ATTRIBUTE.COURSE_CATEGORY]: getCourseType(
            course?.courseTypeId
          ),
          [EVENT_ATTRIBUTE.COURSE_ID]: course?.courseId,
          [EVENT_ATTRIBUTE.COURSE_NAME]: course?.courseTitle,
          [EVENT_ATTRIBUTE.COURSE_TYPE]: getCourseType(course?.courseTypeId),
          [EVENT_ATTRIBUTE.AUTOPAY_ENABLE]: course?.autoPayEnabled ? 1 : 0,
          [EVENT_ATTRIBUTE.COURSE_ONLINE]: mode === "online" ? "yes" : "no",
          [EVENT_ATTRIBUTE.SOURCE_SCREEN]: sourceScreen,
          [EVENT_ATTRIBUTE.ACTION]: action === 2 ? "Renew" : "Fresh",
        });
        trackMoengageEvent(EVENT_NAME.PDP_ENGAGEMENT, {
          [EVENT_ATTRIBUTE.COURSE_ID]: course?.courseId,
          [EVENT_ATTRIBUTE.PDP_TIME_SPENT]: Math.round(
            (Date.now() - courseTimeStamp.current) / 1000
          ),
          [EVENT_ATTRIBUTE.SCREEN_NAME]: "pdp",
        });
      } catch (error) {
        console.error("Error tracking Moengage event:", error);
      }

      router.push(
        `/course-detail/${params.slug}/checkout?${queryParams.toString()}`
      );
    };

  const handleAvailWithEmiClick = (event) => {
    if (!isAuthenticated) {
      event.preventDefault();
      setShowLogin(true);
      return;
    }

    setIsOpen(true);

    // Send Avail with EMI Clicked Event to Moengage
    trackMoengageEvent(EVENT_NAME.AVAIL_WITH_EMI_CLICKED, {
      [EVENT_ATTRIBUTE.COURSE_ID]: course?.courseId,
      [EVENT_ATTRIBUTE.COURSE_NAME]: course?.courseTitle,
      [EVENT_ATTRIBUTE.COURSE_TYPE]: getCourseType(course?.courseTypeId),
      [EVENT_ATTRIBUTE.IS_COURSE_FREE]: isCourseFree,
      [EVENT_ATTRIBUTE.SOURCE_SCREEN]: sourceScreen || "deeplink",
      [EVENT_ATTRIBUTE.LANGUAGE_ID]: course?.defaultLanguageId,
      [EVENT_ATTRIBUTE.COURSE_SELLINGPRICE]:
        course?.packageDiscountPrice2 || course?.packageDiscountPrice1,
      [EVENT_ATTRIBUTE.COURSE_CATEGORY]: getCourseType(course?.courseTypeId),
      [EVENT_ATTRIBUTE.LANGUAGE_SELECTED]: languageMapping(
        course?.defaultLanguageId
      ),
    });
  };

  if (
    isAuthenticated &&
    (isIdleUserCourseDetail || isFetchingUserCourseDetail)
  ) {
    return (
      <div className="flex gap-x-3 w-full">
        <Skeleton className="h-11 grow basis-0" />
        <Skeleton className="h-11 grow basis-0" />
      </div>
    );
  }

  if (isPurchased && isExpiring && paymentType !== 3) {
    return (
      <div className="flex gap-x-3 w-full">
        <Button className="grow basis-0" onClick={handleBuyNowClick(3)}>
          Renew Course
        </Button>
      </div>
    );
  }

  if (isPurchased && isExpired && paymentType !== 3) {
    return (
      <div className="flex gap-x-3 w-full">
        <Button className="grow basis-0" onClick={handleBuyNowClick(2)}>
          Renew Course
        </Button>
      </div>
    );
  }

  const handleStartMyCourseClick = () => {
    const { courseId, languageId } = form.getValues();
    if (!courseId || !languageId) return;

    let courseStatus = "fully_paid";
    if (isCourseFree) {
      courseStatus = "free";
    } else if (userCourseDetail?.paymentType === 1) {
      courseStatus = "renew";
    } else if (userCourseDetail?.paymentType === 2) {
      courseStatus = "EMI";
    } else if (userCourseDetail?.paymentType === 3) {
      courseStatus = "recurring";
    }

    // Send Start My Course Event to Moengage
    trackMoengageEvent(EVENT_NAME.START_MY_COURSE, {
      [EVENT_ATTRIBUTE.COURSE_ID]: course?.courseId,
      [EVENT_ATTRIBUTE.COURSE_NAME]: course?.courseTitle,
      [EVENT_ATTRIBUTE.COURSE_TYPE]: getCourseType(course?.courseTypeId),
      [EVENT_ATTRIBUTE.COURSE_STATUS]: courseStatus,
      [EVENT_ATTRIBUTE.COURSE_CATEGORY]: getCourseType(course?.courseTypeId),
      [EVENT_ATTRIBUTE.EXPIRES_IN]:
        userCourseDetail?.validityLeftDays >= 0
          ? userCourseDetail?.validityLeftDays
          : "free",
      [EVENT_ATTRIBUTE.IS_LIVE]: "pdp",
    });

    let courseConsmeLink = `/course-content/${courseId}-${languageId}/${params.slug}`;

    if (userCourseDetail?.todaysScheduledLiveClass) {
      courseConsmeLink += "/live-class";
    }

    router.push(courseConsmeLink);
  };

  if ((isPurchased && validityLeftDays >= 0) || isCourseFree) {
    return (
      <div className="flex gap-x-3 w-full">
        <Button className="grow basis-0" onClick={handleStartMyCourseClick}>
          Start My Course
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-x-3 w-full">
      {course?.packageDiscountPrice2 >= 20000 && (
        <Button
          variant="outline"
          className="grow basis-0"
          onClick={handleAvailWithEmiClick}
        >
          <div className="flex flex-col">
            <Paragraph variant="b4-tight" weight="sb" className="text-inherit">
              Avail with EMI
            </Paragraph>

            <Text variant="misc-regular">9 Months Plan</Text>
          </div>
        </Button>
      )}

      <Button className="grow basis-0" onClick={handleBuyNowClick(1)}>
        Buy Now
      </Button>
    </div>
  );
}

export default CourseActionButton;
